// src/components/Steps/ChargeMonitoring/ChargeMonitoring.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { ActionButtonWrapper, EvHeader, LabelValueRow, InfoPanel, Notice } from '../../../components';
import { FiBatteryCharging } from 'react-icons/fi';
import { FaCircle } from 'react-icons/fa';
import { useSession } from '../../../context/SessionContext';
import { useNotification } from '../../../context/NotificationContext';
import { useToast } from '../../../context/ToastContext';
import useElementHeight from '../../../hooks/useElementHeight';
import useLiveEstimate, { LIVE_ESTIMATE_POLL_PER_MINUTE_MS } from '../../../hooks/useLiveEstimate';
import usePaymentRecoveryPrompt from '../../../hooks/usePaymentRecoveryPrompt';
import PaymentRecoveryPrompt from '../../PaymentRecoveryPrompt/PaymentRecoveryPrompt';
import { startSession, stopSession, normalizeApiError, getActiveSessionForConnector, getCustomerSession } from '../../../api/sessionsApi';
import { STEPS } from '../../../constants/steps';
import { PAYMENTS_ENABLED } from '../../../config/features';
import { describeLiveEstimate, isBudgetAlmostReached, budgetAlmostReachedLead } from '../../../utils/liveEstimate';
import { formatElapsedTime } from '../../../utils/formatElapsedTime';
import { saveActiveSession, getActiveSession, clearActiveSession } from '../../../utils/sessionPersistence';
import { getLiveSessionEnergy } from '../../../utils/sessionEnergy';
import { getSafeEnergyDisplay } from '../../../utils/safeEnergyDisplay';
import { formatCurrency } from '../../../utils/helpers';
import {
  LIFECYCLE_STATES,
  hasActiveMeterActivity,
} from '../../../utils/sessionLifecycle';
import { decideStart, readStartFlag, writeStartFlag, clearStartFlag } from '../../../utils/startDecision';
import { trackStartSkipped, trackStartRequestSent, trackStartResult } from '../../../telemetry/chargeStart';
import './ChargeMonitoring.less';
import { deriveChargingPresentation, PAUSED_KINDS } from './chargingPresentation.js';
import LiveCanonicalStatus from '../../domain/CanonicalSessionPanel/LiveCanonicalStatus';
import { shouldKeepPolling } from '../../../chargerUx/canonical';
import { getSnapshot } from '../../../chargerUx/canonicalStore';
import { presentSnapshot } from '../../../chargerUx/copy';
import { useCanonicalSession } from '../../../chargerUx/useCanonicalSession';

/**
 * Preparing-state polling backoff schedule (Phase 4a).
 *
 * Charger handoff typically completes within ~30s; after that, polling
 * faster doesn't help. We ramp the poll interval up so we stay light on
 * the API while still catching backend-side expiry.
 *
 * Exported so tests can assert the schedule has not regressed.
 */
export const PREPARING_BACKOFF_SCHEDULE_MS = [10000, 10000, 15000, 15000, 20000];

/** Warn the user after this many minutes in Preparing (UI banner only). */
export const PREPARING_WARNING_MINUTES = 2;

/** Hard auto-cancel after this many minutes in Preparing (safety backstop). */
export const PREPARING_TIMEOUT_MINUTES = 8;

/** Animated ellipsis text for "Receiving data...", "Measuring...", etc. */
const LoadingText = ({ text }) => (
  <span className="cm-loading-text">{text}<span className="cm-loading-dots" /></span>
);

/**
 * Translate a backend session terminal-state into a user-facing
 * "Unable to start charging" modal message. The backend's
 * `failure_reason` / `cancel_reason` columns are the source of truth
 * — when present they override the generic "did not respond in time"
 * string the polling layer used to show for every failure mode.
 *
 * Exported for unit tests so the contract can be pinned without
 * standing up the whole charge-monitoring component.
 */
/**
 * A 401 SESSION_AUTH_REQUIRED says only that this browser can't prove it
 * owns the session (the recovery cookie is missing or stale). It says
 * nothing about the session itself. The shared interceptor already swaps
 * in the "Recover with a code" screen. Marking the session failed here
 * persisted `sessionStartFailed`, so after a successful recovery the
 * driver landed on "Session expired" for a session that was still fine.
 */
export function isSessionAuthRefusal(result) {
  return result?.error === 'SESSION_AUTH_REQUIRED';
}

export function describeStartFailure({
  failure_reason: failureReason = null,
  cancel_reason: cancelReason = null,
  status = null,
  error = null,
  notFound = false,
} = {}) {
  // User initiated cancel — don't dress this up as an error.
  if (cancelReason) {
    return 'Charging start was cancelled.';
  }

  // Specific backend reasons → specific guidance. Keep the wording
  // short and action-oriented; the modal already shows a "Check the
  // cable connection" subline so we don't duplicate that here.
  switch (failureReason) {
    case 'ocpp_start_failed':
    case 'charger_unreachable':
      return "We couldn't reach the charger. Please try again, or pick another charger.";
    case 'connector_busy':
    case 'connector_occupied':
      return 'This connector is already in use. Please pick another charger.';
    case 'charger_offline':
      return 'This charger is offline. Please try another charger.';
    case 'charger_faulted':
    case 'charger_unavailable':
      return 'This charger is currently unavailable.';
    case 'remote_start_rejected':
      return 'The charger rejected the start request. Make sure the cable is connected and try again.';
    case 'cable_not_connected':
    case 'cable_check_failed':
      return 'Please make sure the charging cable is fully connected, then try again.';
    case 'preparing_timeout':
    case 'orphan_cleanup_preparing':
      return "The charger didn't start in time. Please make sure the cable is connected, then try again.";
    case 'payment_failed':
    case 'payment_voided':
      return 'Payment could not be completed. Please try again.';
    default:
      break;
  }

  // No backend reason — distinguish 404 (session evicted from DB)
  // from terminal-status fallbacks so users see consistent wording
  // for both the orphan-cleanup path and a true expiry.
  if (notFound || error === 'SESSION_NOT_FOUND') {
    return "We couldn't find your session anymore. Please start a new one.";
  }
  if (status === 'voided' || status === 'cancelled') {
    return 'Charging start was cancelled.';
  }
  if (status === 'failed' || status === 'expired') {
    return "The charger didn't respond in time. Please make sure the cable is connected, then try again.";
  }

  // Last resort — same wording as before the per-reason map so the
  // change is purely additive at the unknown-reason tail.
  return 'Session expired. The charger did not respond in time.';
}

const ChargeMonitoring = () => {
  const session = useSession();
  const { state, reset, setStep, update, cancelSession, rehydrateSession } = session;
  const { notify } = useNotification();
  const { showToast } = useToast();
  const { ref, height } = useElementHeight();
  const hasStartedCharging = useRef(false);
  const [startFailedError, setStartFailedError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [preparingTimeoutWarning, setPreparingTimeoutWarning] = useState(false);
  const [showForceEnd, setShowForceEnd] = useState(false);
  const previousStatusRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const hasRehydrated = useRef(false);
  const preparingStartTime = useRef(null);
  const preparingTimeoutRef = useRef(null); // Timeout for preparing state monitoring
  const budgetAutoStopFired = useRef(false); // Prevents duplicate auto-stop for per-minute budget
  // Anchors the "No energy detected" banner to THIS mount's observation window
  // rather than the session's historical started_at. Without this, a rehydration
  // of an in-flight session would compute elapsedMs from started_at (potentially
  // many minutes ago) and fire the banner instantly, before SSE could land a
  // single MeterValues frame.
  const monitoringStartedAt = useRef(Date.now());
  
  // GUARD: Ensure sessionBudgetCents matches authorizedAmount after payment
  // This catches stale budgets from session creation (before payment was authorized)
  useEffect(() => {
    if (state?.authorizedAmount && state.authorizedAmount > 0 &&
        state.sessionBudgetCents !== state.authorizedAmount) {
      console.warn('[ChargeMonitoring] Budget mismatch detected — correcting', {
        stale: state.sessionBudgetCents,
        correct: state.authorizedAmount
      });
      update({ sessionBudgetCents: state.authorizedAmount });
    }
  }, [state?.authorizedAmount, state?.sessionBudgetCents]);

  // Live elapsed time ticker (updates every second)
  const [elapsedMs, setElapsedMs] = useState(0);
  
  // Live timer: update elapsed time every second
  useEffect(() => {
    const startTime = state?.live?.startTime;
    const endTime = state?.live?.endTime;
    
    // If no startTime, reset to 0
    if (!startTime) {
      setElapsedMs(0);
      return;
    }

    // Parse startTime to milliseconds
    const startTimeMs = new Date(startTime).getTime();
    if (isNaN(startTimeMs)) {
      setElapsedMs(0);
      return;
    }

    // If session has ended, freeze the elapsed time at final value
    if (endTime) {
      const endTimeMs = new Date(endTime).getTime();
      if (!isNaN(endTimeMs)) {
        const finalElapsed = endTimeMs - startTimeMs;
        setElapsedMs(finalElapsed);
        return; // Don't set up interval - timer is frozen
      }
    }

    // Calculate initial elapsed time
    const updateElapsed = () => {
      const now = Date.now();
      const elapsed = now - startTimeMs;
      setElapsedMs(elapsed);
    };

    // Update immediately
    updateElapsed();

    // Set up interval to update every second (only when session is active)
    const intervalId = setInterval(updateElapsed, 1000);

    // Cleanup interval on unmount or when startTime/endTime changes
    return () => {
      clearInterval(intervalId);
    };
  }, [state?.live?.startTime, state?.live?.endTime]);

  // ============================================================
  // PART 2: Session Rehydration on Mount
  // ============================================================
  useEffect(() => {
    // Server status read on mount; attemptStart decides from it (utils/startDecision).
    let backendStatusAtMount = null;

    const rehydrateOrStart = async () => {
      // Prevent duplicate calls
      if (hasRehydrated.current) return;
      hasRehydrated.current = true;

      console.log('[ChargeMonitoring] Component mounted - checking for session rehydration');

      // STEP 1: Check if we have sessionUid in context
      if (state.sessionUid) {
        console.log('[ChargeMonitoring] SessionUid exists in context:', state.sessionUid);
        
        // ============================================================
        // CRITICAL: Check backend authoritative status FIRST
        // Prevents start loop when backend already says charging
        // ============================================================
        console.log('[ChargeMonitoring] 🔍 Checking backend session status before start attempt...');
        
        try {
          const backendResult = await getCustomerSession(state.sessionUid);
          
          if (backendResult.ok && backendResult.session) {
            const backendStatus = backendResult.session.status;
            backendStatusAtMount = backendStatus;
            
            console.log('[ChargeMonitoring] 📡 Backend session status:', backendStatus);
            
            // BACKEND AUTHORITATIVE CHECK
            if (backendStatus === 'charging' || backendStatus === 'starting') {
              console.log('[ChargeMonitoring] ⚠️ BACKEND ALREADY CHARGING - skipping start, entering recovery mode');
              
              // Mark as started to prevent duplicate calls
              hasStartedCharging.current = true;
              
              // Save active session to localStorage
              saveActiveSession({
                sessionUid: state.sessionUid,
                stationId: state.stationId,
                connectorId: state.connectorId,
                startedAt: state.live?.startTime || backendResult.session.started_at,
                createdAt: state.createdAt
              });
              
              // Force rehydration to sync UI with backend state
              await rehydrateSession();
              
              return;
            }
            
            // A start already in flight on the server (RemoteStart sent,
            // StartTransaction not yet in): never send a second one, and never
            // invent a transaction or start time — StartTransaction / MeterValues
            // bring the real ones.
            if (decideStart({ backendStatus, flagPresent: false }).reason === 'backend_in_flight') {
              console.log('[ChargeMonitoring] Start already in flight on the server - waiting for StartTransaction', { backendStatus });
              hasStartedCharging.current = true;
              trackStartSkipped('monitor', 'backend_in_flight');
              saveActiveSession({
                sessionUid: state.sessionUid,
                stationId: state.stationId,
                connectorId: state.connectorId,
                startedAt: state.live?.startTime || backendResult.session.started_at || null,
                createdAt: state.createdAt
              });
              return;
            }

            // Check for terminal failure states
            if (backendStatus === 'expired' || backendStatus === 'failed' || backendStatus === 'voided') {
              console.log('[ChargeMonitoring] ❌ Backend session in terminal state:', backendStatus);
              setStartFailedError({
                message: `Session ${backendStatus}. Please start a new session.`,
                correlationId: backendResult.correlationId
              });
              update({ sessionStartFailed: true });
              return;
            }
          }
        } catch (error) {
          console.error('[ChargeMonitoring] Failed to check backend status:', error);
          // Continue with normal flow - don't block on backend check failure
        }
        
        // Local state check - only treat as already started with hard proof
        const status = (state.live?.status || '').toLowerCase();
        const isAlreadyStarted = 
          Boolean(state.live?.tx_id) || 
          Boolean(state.live?.startTime) || 
          status === 'charging';
        
        if (isAlreadyStarted) {
          console.log('[ChargeMonitoring] resume: session already started locally; skipping start call', {
            startTime: state.live?.startTime,
            tx_id: state.live?.tx_id,
            status: state.live?.status
          });
          
          // Mark as started to prevent duplicate calls
          hasStartedCharging.current = true;
          
          // Save active session to localStorage
          saveActiveSession({
            sessionUid: state.sessionUid,
            stationId: state.stationId,
            connectorId: state.connectorId,
            startedAt: state.live?.startTime,
            createdAt: state.createdAt
          });
          
          // Resume monitoring quietly - do NOT show noisy toast on every refresh
          return;
        }
        
        // Session exists but not yet started - proceed with normal start flow
        console.log('[ChargeMonitoring] Session exists but not started - proceeding with start', {
          status: state.live?.status
        });
        
        // Save active session to localStorage
        saveActiveSession({
          sessionUid: state.sessionUid,
          stationId: state.stationId,
          connectorId: state.connectorId,
          startedAt: state.live?.startTime,
          createdAt: state.createdAt
        });

        // Proceed with normal start flow
        await attemptStart();
        return;
      }

      // STEP 2: No sessionUid in context - check localStorage for recovery
      console.log('[ChargeMonitoring] No sessionUid in context - checking localStorage');
      const savedSession = getActiveSession();

      if (!savedSession) {
        console.log('[ChargeMonitoring] No saved session found - redirecting to start');
        notify({
          title: 'Session Not Found',
          message: 'No active session found. Please start a new charging session.',
        });
        reset();
        setStep(STEPS.EV_PROFILE);
        return;
      }

      console.log('[ChargeMonitoring] Found saved session - attempting rehydration:', savedSession);
      setIsReconnecting(true);

      try {
        // STEP 3: Verify session is still active on server
        const activeSessionResult = await getActiveSessionForConnector(
          savedSession.stationId,
          savedSession.connectorId
        );

        if (!activeSessionResult.ok) {
          console.error('[ChargeMonitoring] Failed to check active session:', activeSessionResult.error);
          clearActiveSession();
          notify({
            title: 'Session Recovery Failed',
            message: 'Could not reconnect to session. Please start a new session.',
          });
          reset();
          setStep(STEPS.EV_PROFILE);
          return;
        }

        const activeSession = activeSessionResult.activeSession;

        if (!activeSession) {
          console.log('[ChargeMonitoring] No active session found on server - clearing localStorage');
          clearActiveSession();
          notify({
            title: 'Session Ended',
            message: 'Your previous session has ended. Please start a new session.',
          });
          reset();
          setStep(STEPS.EV_PROFILE);
          return;
        }

        // STEP 4: Active session found - rehydrate context from server
        console.log('[ChargeMonitoring] Active session found - rehydrating:', activeSession.session_uid);
        
        const rehydrationResult = await rehydrateSession();
        
        if (!rehydrationResult.success) {
          console.error('[ChargeMonitoring] Rehydration failed:', rehydrationResult.reason);
          clearActiveSession();
          notify({
            title: 'Session Recovery Failed',
            message: 'Could not restore session. Please start a new session.',
          });
          reset();
          setStep(STEPS.EV_PROFILE);
          return;
        }

        console.log('[ChargeMonitoring] ✓ Session rehydrated successfully');
        
        // Check if rehydrated session is already started
        const rehydratedStatus = (state.live?.status || '').toLowerCase();
        const rehydratedIsStarted = 
          Boolean(state.live?.tx_id) || 
          Boolean(state.live?.startTime) || 
          rehydratedStatus === 'charging';
        
        if (rehydratedIsStarted) {
          console.log('[ChargeMonitoring] resume: session already started; skipping start call');
          hasStartedCharging.current = true;
        }
        
      } catch (error) {
        console.error('[ChargeMonitoring] Exception during rehydration:', error);
        clearActiveSession();
        notify({
          title: 'Session Recovery Failed',
          message: 'An error occurred while restoring your session.',
        });
        reset();
        setStep(STEPS.EV_PROFILE);
      } finally {
        setIsReconnecting(false);
      }
    };

    const attemptStart = async () => {
      // Prevent duplicate calls
      if (hasStartedCharging.current) return;

      // Whether a start is still needed comes from the server status read on
      // mount; the per-tab flag only decides when the server could not be
      // asked. A stale flag (an earlier page's /start never landed) must not
      // veto a start the server says is still needed — the API claim is
      // idempotent.
      if (state.sessionUid) {
        const flagPresent = readStartFlag(state.sessionUid);
        const decision = decideStart({ backendStatus: backendStatusAtMount, flagPresent });
        if (decision.action === 'skip') {
          console.log('[ChargeFlow] Not sending /start', { sessionUid: state.sessionUid, backendStatus: backendStatusAtMount, reason: decision.reason });
          trackStartSkipped('monitor', decision.reason);
          hasStartedCharging.current = true;
          return;
        }
        if (flagPresent) clearStartFlag(state.sessionUid);
      }

      // Guard: Prevent starting session when payment is required but not authorized
      // Check both canonical (state.vehicle) and legacy (state.session.vehicle) paths.
      const paymentRequired =
        state.vehicle?.paymentRequired === true ||
        state.session?.vehicle?.paymentRequired === true;
      const billingMode =
        state.vehicle?.billingMode ||
        state.session?.vehicle?.billingMode ||
        'UNKNOWN';
      
      if (PAYMENTS_ENABLED && paymentRequired && !state.payment?.authorizationId) {
        console.error('[ChargeFlow] Cannot start charging: payment authorization required', {
          paymentRequired,
          billingMode,
          hasAuthorizationId: !!state.payment?.authorizationId
        });
        trackStartSkipped('monitor', 'payment_required');
        setStep(STEPS.AMOUNT_PICKER);
        notify({
          title: 'Payment Required',
          message: 'Payment authorization is required. Redirecting to payment...',
        });
        return;
      }
      
      // Log payment check result for billing modes that don't require payment
      if (PAYMENTS_ENABLED && !paymentRequired) {
        console.log('[ChargeFlow] paymentRequired=false, billingMode=' + billingMode + ', skipping payment auth check');
      }
      
      // CRITICAL: Must have session_uid to start charging
      if (!state.sessionUid) {
        console.error('[ChargeFlow] Cannot start charging: session_uid is required');
        notify({
          title: 'Session Not Found',
          message: 'Session not initialized. Please restart the charging flow.',
          action: {
            label: 'OK',
            onClick: () => {
              reset();
              setStep(STEPS.EV_PROFILE);
            }
          }
        });
        return;
      }

      hasStartedCharging.current = true;

      try {
        console.log('[ChargeFlow] Starting session', {
          sessionUid: state.sessionUid,
          hasAuthorizationId: !!state.payment?.authorizationId,
          timestamp: new Date().toISOString()
        });
        
        trackStartRequestSent('monitor');
        const result = await startSession(state.sessionUid);
        trackStartResult('monitor', result);
        
        if (result.ok) {
          console.log('[ChargeFlow] Start successful');

          // Mark this session as started in sessionStorage so a remount or a
          // back-nav into ConnectCharger does not issue /start again.
          writeStartFlag(state.sessionUid);

          // No startTime is written here: an accepted /start is not a started
          // transaction. StartTransaction (or the first MeterValues) sets it;
          // an invented one made a later Available read as "charging ended".
          saveActiveSession({
            sessionUid: state.sessionUid,
            stationId: state.stationId,
            connectorId: state.connectorId,
            startedAt: state.live?.startTime || null,
            createdAt: state.createdAt
          });
        } else {
          // Start failed - normalize error and show modal
          const normalizedError = normalizeApiError({ response: { data: result } }, result.correlationId);
          console.error('[ChargeFlow] Start failed:', normalizedError);
          hasStartedCharging.current = false;

          // Not a start failure: the recovery screen takes over.
          if (isSessionAuthRefusal(result)) return;
          
          // Mark session as failed_start to prevent loops
          update({ sessionStartFailed: true });
          
          // Show error modal with retry/cancel options
          setStartFailedError(normalizedError);
        }
      } catch (error) {
        console.error('[ChargeFlow] Exception during start:', error);
        trackStartResult('monitor', { ok: false, error: 'EXCEPTION', httpStatus: null });
        hasStartedCharging.current = false;
        
        // Mark session as failed_start
        update({ sessionStartFailed: true });
        
        const normalizedError = normalizeApiError(error, null);
        setStartFailedError(normalizedError);
      }
    };

    rehydrateOrStart();
  }, []); // Empty dependency array - only run once on mount

  // Handle retry start charging
  const handleRetry = async () => {
    if (isRetrying) return;

    console.log('[ChargeFlow] Retrying start charging');
    setIsRetrying(true);
    setStartFailedError(null);
    hasStartedCharging.current = false; // Reset flag to allow retry

    // ConnectCharger writes ev.start.called.{uid} to sessionStorage to
    // suppress a duplicate /start on remount. If that key still exists
    // on retry, a subsequent navigation back to ConnectCharger would
    // skip the start request entirely — leaving the user staring at
    // the modal forever. Clear the guard before re-firing.
    if (state.sessionUid) clearStartFlag(state.sessionUid);
    // Also clear the sessionStartFailed flag so other downstream
    // guards (ConnectCharger pre-check) don't refuse a fresh attempt.
    update({ sessionStartFailed: false });

    try {
      trackStartRequestSent('retry');
      const result = await startSession(state.sessionUid);
      trackStartResult('retry', result);
      
      if (result.ok) {
        console.log('[ChargeFlow] Retry successful');
        // No invented startTime — see attemptStart.
        update({ sessionStartFailed: false });
        hasStartedCharging.current = true;
        showToast({ message: 'Charging started successfully', type: 'success' });
      } else {
        // Retry failed
        const normalizedError = normalizeApiError({ response: { data: result } }, result.correlationId);
        console.error('[ChargeFlow] Retry failed:', normalizedError);
        setStartFailedError(normalizedError);
      }
    } catch (error) {
      console.error('[ChargeFlow] Exception during retry:', error);
      trackStartResult('retry', { ok: false, error: 'EXCEPTION', httpStatus: null });
      const normalizedError = normalizeApiError(error, null);
      setStartFailedError(normalizedError);
    } finally {
      setIsRetrying(false);
    }
  };

  // ============================================================
  // Handle cancel session
  // CRITICAL: Check backend status before allowing cancel
  // If backend says charging, must use stop instead
  // ============================================================
  const handleCancelSession = async () => {
    if (isCancelling) return;
    
    console.log('[ChargeFlow] Canceling session due to start failure');
    setIsCancelling(true);
    
    try {
      // CRITICAL: Check backend status before cancel
      if (state.sessionUid) {
        const backendCheck = await getCustomerSession(state.sessionUid);
        
        if (backendCheck.ok && backendCheck.session) {
          const backendStatus = backendCheck.session.status;
          
          // If backend says charging/starting, cannot cancel
          if (backendStatus === 'charging' || backendStatus === 'starting') {
            console.error('[ChargeFlow] ⚠️ Cannot cancel - backend session is already charging');
            showToast({
              message: 'Session is already charging. Please use Stop Charging instead.',
              type: 'error'
            });
            setIsCancelling(false);
            setStartFailedError(null);
            
            // Force rehydration to show stop button
            await rehydrateSession();
            return;
          }
        }
      }
      
      const result = await cancelSession();
      
      if (result.success) {
        console.log('[ChargeFlow] Session canceled successfully');
        // Silent cleanup - no toast notification
        // cancelSession() already resets state and navigates
      } else {
        // Handle "charging started" error specifically
        if (result.blockReason === 'charging_started') {
          console.error('[ChargeFlow]  Cannot cancel - charging started');
          showToast({
            message: result.error || 'Charging already started — please stop session instead.',
            type: 'error'
          });
          
          // Force rehydration to show stop button
          await rehydrateSession();
        } else {
          console.error('[ChargeFlow]  Cancel failed:', result.error);
          showToast({
            message: result.error || 'Failed to cancel session',
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('[ChargeFlow] Exception during cancel:', error);
      showToast({ 
        message: 'Failed to cancel session', 
        type: 'error' 
      });
    } finally {
      setIsCancelling(false);
      setStartFailedError(null);
    }
  };

  // ============================================================
  // Live energy delivered from session (per-session delta, not cumulative meter)
  // Uses utility to ensure correct calculation from session state
  // DISPLAY GUARD: Apply frontend safety check for live UI stability
  // ============================================================
  const rawEnergyDelivered = getLiveSessionEnergy(state);
  
  // Extract cumulative meter value from OCPP meter_value array for fallback
  const extractCumulativeMeter = (meterValue) => {
    if (!Array.isArray(meterValue)) return null;
    
    for (const reading of meterValue) {
      if (reading.sampled_value && Array.isArray(reading.sampled_value)) {
        for (const sample of reading.sampled_value) {
          if (sample.measurand === 'Energy.Active.Import.Register') {
            const value = parseFloat(sample.value) || 0;
            return sample.unit === 'Wh' ? value / 1000 : value;
          }
        }
      }
    }
    return null;
  };
  
  const latestMeterKwh = extractCumulativeMeter(state?.live?.meter_value);
  
  // Apply display safety guard (frontend-only, does not affect backend billing)
  const energyDelivered = getSafeEnergyDisplay({
    energyKwh: rawEnergyDelivered,
    meterStartKwh: state?.live?.meter_start,
    latestMeterKwh: latestMeterKwh
  });

  // Estimated cost, charge rate and time left are the backend's
  // (greencharge.live-estimate.v1, sw-ev-api docs/SESSION_LIVE_ESTIMATE.md).
  // This screen prints them; it does not work out money, a rate or a time
  // from the pricing snapshot or from the meter frames it happens to receive.
  // Until an estimate arrives (first load, a reload, a failed read) the rows
  // show "Calculating".
  const { liveEstimate } = useLiveEstimate({
    sessionUid: state?.sessionUid || null,
    energyKwh: energyDelivered,
    ended: !!state?.live?.endTime,
    pollMs: state?.pricingSnapshot?.billing_unit === 'minute' ? LIVE_ESTIMATE_POLL_PER_MINUTE_MS : undefined,
  });
  // Payment recovery: is this customer being asked to confirm a card again
  // for this session (a hold that was voided while the charger started)?
  // Asked while the session is active; the API says no for everything else.
  const { prompt: paymentRecoveryPrompt } = usePaymentRecoveryPrompt({
    sessionUid: state?.sessionUid || null,
    ended: !!state?.live?.endTime,
  });
  const storedBillingUnit = state?.pricingSnapshot?.billing_unit || 'kwh';
  const estimate = describeLiveEstimate(liveEstimate, { fallbackBillingUnit: storedBillingUnit });

  // Billing unit for conditional display: the estimate's once there is one,
  // the session's stored unit until then.
  const billingUnit = estimate.billingUnit;

  // ============================================================
  // PART 3: Preparing State Polling - Detect Backend Expiry
  // Polls session status every 10s while in preparing to detect backend expiry
  // ============================================================
  useEffect(() => {
    const currentStatus = state?.live?.status;
    const isPreparingState = currentStatus === 'Preparing';
    
    // Track when preparing state begins
    if (isPreparingState && !preparingStartTime.current) {
      preparingStartTime.current = Date.now();
      console.log('[ChargeMonitoring] Preparing state started - beginning timeout monitoring');
    } else if (!isPreparingState && preparingStartTime.current) {
      preparingStartTime.current = null;
      setPreparingTimeoutWarning(false);
      console.log('[ChargeMonitoring] Preparing state ended');
    }
    
    // Only poll while in preparing state
    if (!isPreparingState || !state.sessionUid) {
      // Clear any existing poll
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }
    
    // Set up polling to detect backend expiry
    const pollSessionStatus = async () => {
      try {
        const result = await getCustomerSession(state.sessionUid);
        
        if (result.notFound || !result.ok) {
          // Not an expiry: the recovery screen takes over, and the poll
          // stops when this component unmounts.
          if (isSessionAuthRefusal(result)) return;
          // If MeterValues are flowing, polling is racing the SSE/backend —
          // do NOT treat this as an expiry. Keep the charging UI; the next
          // poll will recover.
          if (hasActiveMeterActivity(state?.live)) {
            console.log('[ChargeMonitoring] Poll returned preparing but local meter data active — keeping charging', {
              transient: true,
              reason: result.notFound ? 'not_found' : 'fetch_error',
            });
            return;
          }
          console.warn('[ChargeMonitoring] Preparing poll: session not found or error - session may have expired', {
            failure_reason: result.failure_reason,
            cancel_reason: result.cancel_reason,
            status: result.status,
            session_uid: result.session_uid,
          });

          // Session expired/voided on backend - show error and reset.
          // Translate the backend reason into specific UX text; fall
          // back to the generic "did not respond" only when nothing
          // more specific is available.
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;

          setStartFailedError({
            message: describeStartFailure({
              failure_reason: result.failure_reason,
              cancel_reason: result.cancel_reason,
              status: result.status,
              error: result.error,
              notFound: result.notFound,
            }),
            // Prefer the backend session_uid for the support ref —
            // ops can grep prod logs for that. correlationId is a
            // frontend-only value and is only useful as a fallback.
            correlationId: result.session_uid || result.correlationId || 'timeout',
          });

          // Mark as failed to prevent retry loop
          update({ sessionStartFailed: true });
          return;
        }
        
        const serverStatus = (result.session?.status || '').toLowerCase();

        // Check for terminal states that indicate failure
        const terminalFailureStates = ['expired', 'voided', 'failed', 'cancelled'];
        if (terminalFailureStates.includes(serverStatus)) {
          // If we have firsthand MeterValues evidence, the backend is just
          // wrong (a stale cleanup decision). Don't blow up the UI — keep
          // the charging screen and let the OCPP API recovery path resolve.
          if (hasActiveMeterActivity(state?.live)) {
            console.log('[ChargeMonitoring] Poll returned preparing but local meter data active — keeping charging', {
              backendStatus: serverStatus,
              localStatus: state?.live?.status,
              energyKWh: state?.live?.energyKWh,
            });
            return;
          }
          console.warn('[ChargeMonitoring] Preparing poll: session in terminal state', {
            status: serverStatus,
            failure_reason: result.session?.failure_reason,
            cancel_reason: result.session?.cancel_reason,
            session_uid: result.session?.session_uid,
          });

          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;

          setStartFailedError({
            message: describeStartFailure({
              failure_reason: result.session?.failure_reason,
              cancel_reason: result.session?.cancel_reason,
              status: serverStatus,
              error: result.error,
            }),
            correlationId: result.session?.session_uid || result.correlationId || 'timeout',
          });

          // Mark as failed
          update({ sessionStartFailed: true });
          return;
        }
        
        // Check timeout warning (> 2 minutes in preparing)
        if (preparingStartTime.current) {
          const preparingDurationMs = Date.now() - preparingStartTime.current;
          const preparingMinutes = preparingDurationMs / 1000 / 60;
          
          // ═══════════════════════════════════════════════════════════════
          // METER VALUES FLOWING GUARD: If MeterValues have been received
          // (hasReceivedMeterValues flag, energyKWh > 0, recent lastReadingAt,
          // or cumulative > meterStart), the charger IS charging — skip the
          // preparing timeout and force the UI into charging.
          // ═══════════════════════════════════════════════════════════════
          const liveState = state?.live;
          const meterActive = hasActiveMeterActivity(liveState);

          if (meterActive) {
            console.log('[ChargeMonitoring] Timeout ignored because MeterValues active', {
              hasReceivedMeterValues: liveState?.hasReceivedMeterValues,
              energyKWh: liveState?.energyKWh,
              lastMeterValueAt: liveState?.lastMeterValueAt,
              status: liveState?.status,
            });
            if (liveState?.status === 'Preparing' || liveState?.lifecycleState !== LIFECYCLE_STATES.CHARGING) {
              update({
                live: {
                  ...liveState,
                  status: 'Charging',
                  lifecycleState: LIFECYCLE_STATES.CHARGING,
                },
              });
            }
            // Clear preparing tracking since we're effectively charging
            preparingStartTime.current = null;
            setPreparingTimeoutWarning(false);
            // Stop the preparing poll — not needed when charging
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }

          if (preparingMinutes > PREPARING_WARNING_MINUTES && !preparingTimeoutWarning) {
            console.warn('[ChargeMonitoring] Preparing timeout warning: > 2 minutes in preparing state');
            setPreparingTimeoutWarning(true);
          }

          // Auto-cancel if preparing for > 8 minutes (safety backstop)
          // ONLY if NO MeterValues have been received (guarded above)
          if (preparingMinutes > PREPARING_TIMEOUT_MINUTES) {
            // Last-chance re-check: fetch the latest session AND ensure no
            // local MeterValues snuck in during the poll window.
            const lastCheckMeterActive = hasActiveMeterActivity(state?.live);
            if (lastCheckMeterActive) {
              console.log('[ChargeMonitoring] Timeout ignored because MeterValues active (race re-check)', {
                hasReceivedMeterValues: state?.live?.hasReceivedMeterValues,
                energyKWh: state?.live?.energyKWh,
              });
              preparingStartTime.current = null;
              setPreparingTimeoutWarning(false);
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              return;
            }
            console.error('[ChargeMonitoring] Preparing timeout critical: > 8 minutes, auto-canceling');

            // DIAGNOSTIC: this is the only AUTOMATIC backend-cancel inside
            // ChargeMonitoring. The two `hasActiveMeterActivity` guards
            // above it should have caught any live session — if this trace
            // fires during what looked like an active charging session,
            // the meter-evidence flag was missing on the live state.
            console.log('[SESSION_CANCEL_TRACE]', {
              site: 'ChargeMonitoring.preparingTimeout8min',
              sessionUid: state.sessionUid,
              step: state.step,
              liveStatus: state?.live?.status,
              txId: state?.live?.tx_id ?? null,
              hasReceivedMeterValues: state?.live?.hasReceivedMeterValues ?? null,
              energyKWh: state?.live?.energyKWh ?? null,
              paymentStatus: state?.payment?.status ?? null,
              preparingMinutes,
              lastMeterValueAt: state?.live?.lastMeterValueAt ?? null,
              ts: new Date().toISOString()
            });

            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            // Auto-cancel the session
            const cancelResult = await cancelSession();

            setStartFailedError({
              message: 'Session timeout. The charger took too long to respond.',
              // Frontend-side timeout — backend never produced a
              // session_uid-bearing terminal record, so the ref
              // tag carries the local session_uid + a marker.
              correlationId: state.sessionUid
                ? `${state.sessionUid} (timeout-8min)`
                : 'timeout-8min',
            });

            return;
          }
        }
        
        console.log('[ChargeMonitoring] Preparing poll: session healthy, status:', serverStatus);
        
      } catch (error) {
        console.error('[ChargeMonitoring] Preparing poll error:', error);
        // Don't stop polling on transient errors
      }
    };
    
    // Initial poll
    pollSessionStatus();

    // Backoff schedule (Phase 4a). The Preparing state used to poll at a flat
    // 10s for up to 8 minutes (48 requests worst-case). In practice charger
    // handoff happens in the first ~30s; after that we're almost certainly
    // waiting on a slow/unresponsive charger and polling faster doesn't help.
    // Ramp: 10s → 10s → 15s → 15s → 20s (steady). ~30 req/8min worst-case.
    //
    // Note on cancellation: pollSessionStatus contains several terminal paths
    // (notFound, terminal status, 8-min timeout) that null pollingIntervalRef.
    // We detect that by flipping `cancelled` from inside those paths via the
    // ref going null, and stop the chain before re-scheduling.
    const BACKOFF_SCHEDULE_MS = PREPARING_BACKOFF_SCHEDULE_MS;
    let pollCount = 0;
    let cancelled = false;
    const scheduleNext = () => {
      if (cancelled) return;
      const delay = BACKOFF_SCHEDULE_MS[
        Math.min(pollCount, BACKOFF_SCHEDULE_MS.length - 1)
      ];
      const id = setTimeout(async () => {
        if (cancelled) return;
        pollCount += 1;
        await pollSessionStatus();
        // pollSessionStatus terminal paths null the ref; treat that as cancel.
        if (pollingIntervalRef.current == null) {
          cancelled = true;
          return;
        }
        scheduleNext();
      }, delay);
      pollingIntervalRef.current = id;
    };
    scheduleNext();

    // Cleanup on unmount or status change
    return () => {
      cancelled = true;
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [state?.live?.status, state.sessionUid, cancelSession, update]);

  // ============================================================
  // PART 4: Page Visibility API - Rehydrate when app returns from background
  // ============================================================
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[ChargeMonitoring] App returned to foreground - rehydrating session');
        
        // Only rehydrate if we have an active session
        if (state.sessionUid) {
          try {
            const result = await rehydrateSession();
            
            if (result.success) {
              console.log('[ChargeMonitoring] ✓ Session refreshed successfully');
              
              // Update localStorage with fresh data
              saveActiveSession({
                sessionUid: state.sessionUid,
                stationId: state.stationId,
                connectorId: state.connectorId,
                startedAt: state.live?.startTime,
                createdAt: state.createdAt
              });
            } else {
              console.warn('[ChargeMonitoring] Session rehydration failed on visibility change:', result.reason);
            }
          } catch (error) {
            console.error('[ChargeMonitoring] Exception during visibility rehydration:', error);
          }
        }
      } else {
        console.log('[ChargeMonitoring] App went to background');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.sessionUid, state.stationId, state.connectorId, state.live?.startTime, state.createdAt, rehydrateSession]);

  // ============================================================
  // PART 5: Charging Status Poll — Heartbeat to detect backend session end
  // SSE can silently die (network change, server restart, proxy timeout).
  // This poll checks the backend every 30s during active charging.
  // If backend says session ended/stopped/finalized, we update local state
  // so the auto-navigate effect (PART 6) can pick it up.
  // ============================================================
  useEffect(() => {
    // Only run while actively charging
    const isActivelyCharging = state?.live?.status === 'Charging' ||
      (state?.live?.tx_id && !state?.live?.endTime);

    if (!isActivelyCharging || !state.sessionUid) return;

    let cancelled = false;
    const POLL_INTERVAL = 30000; // 30 seconds

    const checkBackendStatus = async () => {
      if (cancelled) return;
      try {
        const result = await getCustomerSession(state.sessionUid);
        // Charger UX: once the canonical state is terminal and the stop is
        // confirmed, nothing further can change — this read was the last.
        if (!shouldKeepPolling(getSnapshot(state.sessionUid))) clearInterval(interval);
        if (cancelled || !result.ok || !result.session) return;

        const backend = result.session;
        const terminalBackendStatuses = ['stopped', 'ended', 'completed', 'finalized', 'failed', 'expired', 'voided'];

        if (terminalBackendStatuses.includes(backend.status)) {
          console.log('[ChargeMonitoring] 🔔 Backend status poll detected session ended:', {
            backendStatus: backend.status,
            frontendStatus: state?.live?.status,
            finalized_at: backend.finalized_at,
            energy_kwh: backend.energy_kwh || backend.final_energy_kwh
          });

          // Update session context with backend end signals so PART 6 triggers
          update({
            live: {
              ...state.live,
              endTime: backend.stopped_at || backend.finalized_at || new Date().toISOString(),
              status: 'Finished',
              energyKWh: backend.final_energy_kwh || backend.energy_kwh || state.live?.energyKWh
            }
          });
        }
      } catch (err) {
        // Non-fatal — SSE or next poll will catch it
        console.warn('[ChargeMonitoring] Charging heartbeat poll error:', err.message);
      }
    };

    console.log('[ChargeMonitoring] 💓 Starting charging heartbeat poll (30s interval)');
    const interval = setInterval(checkBackendStatus, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state?.live?.status, state?.live?.tx_id, state?.live?.endTime, state.sessionUid]);

  // ============================================================
  // PART 5B: Force-End Escape Hatch Timer
  // If stop was requested but charger hasn't responded within 30s,
  // show the "Force End" button so user can bypass the charger.
  // ============================================================
  useEffect(() => {
    // Backend sets status to 'stopping' on stop request. Check via heartbeat poll.
    // Frontend doesn't see 'stopping' directly — detect via endTime without finalization.
    // Simpler approach: if stop button was pressed (endTime set) but session hasn't
    // navigated to summary within 30s, show force-end.
    if (!state?.live?.endTime || !state.sessionUid) {
      setShowForceEnd(false);
      return;
    }

    const timer = setTimeout(() => {
      console.log('[ChargeMonitoring] Stop request taking >30s — showing Force End option');
      setShowForceEnd(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, [state?.live?.endTime, state.sessionUid]);

  // ============================================================
  // PART 5C: Per-Minute Budget Auto-Stop (frontend backup)
  // Server-side budget monitor is the primary enforcer.
  // This is a best-effort backup in case the browser is still open.
  // ============================================================
  useEffect(() => {
    if (billingUnit !== 'minute') return;
    if (!estimate.budgetReached) return; // the backend's word, not a local sum
    if (budgetAutoStopFired.current) return;
    if (!state?.sessionUid) return;
    if (state?.live?.endTime) return; // Already stopping/stopped

    budgetAutoStopFired.current = true;
    console.log('[ChargeMonitoring] Per-minute budget reached — triggering auto-stop');

    stopSession(state.sessionUid).then(result => {
      if (result.ok) {
        showToast({ message: 'Session budget reached — stopping charger.', type: 'info' });
        // Set endTime to trigger normal completion flow
        update({
          live: {
            ...state.live,
            endTime: state.live.endTime || new Date().toISOString()
          }
        });
      } else {
        console.warn('[ChargeMonitoring] Auto-stop failed:', result);
        // Server-side budget monitor will handle it
      }
    }).catch(err => {
      console.warn('[ChargeMonitoring] Auto-stop error:', err.message);
    });
  }, [billingUnit, estimate.budgetReached, state?.sessionUid, state?.live?.endTime]);

  // ============================================================
  // PART 6: Auto-Navigate to Summary When Session Ends
  // SSE lifecycle is managed by SessionContext - no manual disconnect needed
  // Cleanup: Clear active session tracking when navigating to summary
  // 
  // CRITICAL FIX: Poll backend to verify session is finalized before navigation
  // This prevents premature navigation while backend session is still:
  // - status: pending
  // - finalized_at: null
  // - summary_total_cost: undefined
  // ============================================================
  useEffect(() => {
    const sessionStatus = state?.live?.status;
    const hasEndTime = !!state?.live?.endTime;
    const hasTxId = !!state?.live?.tx_id;
    const hasStartTime = !!state?.live?.startTime;

    // Terminal session states that indicate the session has ended.
    // SuspendedEV / SuspendedEVSE are deliberately absent: they are pauses
    // inside an open transaction (vehicle or charger withholding power, e.g.
    // battery full or balancing) and charging can resume at any moment. The
    // server never ends a session on a pause (transactionBoundary.js).
    const terminalStatuses = ['Finished', 'Available'];
    
    // PROOF OF CHARGING: Session must have either:
    // 1. Explicit endTime (StopTransaction received)
    // 2. Had a transaction ID at some point (tx_id or startTime exists)
    // 3. Delivered actual energy
    const hadChargingSession = hasTxId || hasStartTime || (state?.live?.energyKWh > 0);
    
    // Check if frontend signals session has ended
    const frontendSessionEnded = hasEndTime || (sessionStatus && terminalStatuses.includes(sessionStatus) && hadChargingSession);

    if (!frontendSessionEnded || !state.sessionUid) {
      // Session not ended yet or no sessionUid - nothing to do
      return;
    }

    // Frontend says session ended - now verify backend is finalized
    console.log('[ChargeMonitoring] 🛑 Frontend session ended detected - verifying backend finalization:', {
      status: sessionStatus,
      hasEndTime,
      hasTxId,
      hasStartTime,
      energyKWh: state?.live?.energyKWh,
      hadChargingSession,
      sessionUid: state.sessionUid,
      trigger: hasEndTime ? 'endTime' : 'status',
      billingMode: state?.vehicle?.billingMode
    });

    // Poll backend to check if session is finalized
    let pollInterval = null;
    let pollAttempts = 0;
    const MAX_POLL_ATTEMPTS = 30; // 30 seconds max wait
    
    // Terminal-status gate.
    //
    // SessionSummary (receipt / rating / Start New Session) can ONLY
    // render after the backend confirms a terminal status. The three
    // "give up and jump to SUMMARY" paths this handler used to have
    // all violated that rule. When any of them fires now we forward
    // to DISCONNECT_CHARGER instead — its own bounded poll continues
    // watching the backend for confirmation AND surfaces the explicit
    // "End billing session" (Force End) affordance in its fallback
    // state. This preserves the customer's recovery path without ever
    // leaking a fake completion.
    const advanceToWaiting = (why) => {
      console.warn(`[ChargeMonitoring] ${why} — forwarding to DisconnectCharger (waiting-for-confirmation)`);
      clearInterval(pollInterval);
      // DELIBERATELY do NOT clearActiveSession here — the session
      // is not confirmed terminal yet, and DisconnectCharger's poll
      // needs sessionUid to keep confirming.
      setStep(STEPS.DISCONNECT_CHARGER);
    };

    const checkBackendFinalization = async () => {
      try {
        pollAttempts++;
        console.log(`[ChargeMonitoring] 🔍 Polling backend finalization (attempt ${pollAttempts}/${MAX_POLL_ATTEMPTS})...`);

        const result = await getCustomerSession(state.sessionUid);

        if (!result.ok || !result.session) {
          advanceToWaiting('Backend session fetch failed');
          return;
        }

        const backendSession = result.session;

        // Authoritative backend finalization signals — the ONLY path
        // this handler takes to SUMMARY.
        const isFinalized =
          backendSession.finalized_at != null ||
          backendSession.is_finalized === true ||
          backendSession.status === 'completed' ||
          backendSession.status === 'finalized' ||
          backendSession.status === 'stopped';

        console.log(`[ChargeMonitoring] 📊 Backend status check:`, {
          status: backendSession.status,
          finalized_at: backendSession.finalized_at,
          is_finalized: backendSession.is_finalized,
          has_summary_total_cost: backendSession.summary_total_cost != null,
          has_summary_ended_at: !!backendSession.summary_ended_at,
          isFinalized
        });

        if (isFinalized) {
          console.log('[ChargeMonitoring] ✅ Backend session finalized - navigating to summary');
          clearInterval(pollInterval);
          clearActiveSession();
          setStep(STEPS.SUMMARY);
        } else if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          advanceToWaiting('Backend finalization timeout');
        } else {
          console.log(`[ChargeMonitoring] ⏳ Backend not finalized yet, will retry in 1s...`);
        }
      } catch (error) {
        console.error('[ChargeMonitoring] ❌ Error checking backend finalization:', error);
        if (pollAttempts >= MAX_POLL_ATTEMPTS) {
          advanceToWaiting('Backend poll errored past max attempts');
        }
      }
    };
    
    // Start polling immediately, then every 1 second
    checkBackendFinalization();
    pollInterval = setInterval(checkBackendFinalization, 1000);
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [state?.live?.status, state?.live?.endTime, state?.live?.tx_id, state?.live?.startTime, state?.live?.energyKWh, state.sessionUid, state?.vehicle?.billingMode, setStep]);

  // Log Preparing state transitions (dev only)
  useEffect(() => {
    const currentStatus = state?.live?.status;
    const previousStatus = previousStatusRef.current;

    // Only log when status changes to/from Preparing
    if (currentStatus !== previousStatus) {
      if (currentStatus === 'Preparing') {
        console.log('[MONITOR] station_status=Preparing sessionUid=', state.sessionUid, {
          timestamp: new Date().toISOString(),
          previousStatus,
          stationId: state.stationId
        });
      }
      previousStatusRef.current = currentStatus;
    }
  }, [state?.live?.status, state.sessionUid, state.stationId]);

  // Log live data gate evaluation when key signals change
  useEffect(() => {
    const gateValues = {
      hasReceivedMeterValues: state?.live?.hasReceivedMeterValues ?? false,
      lifecycle: state?.live?.lifecycleState,
      status: state?.live?.status,
      sessionDelta: state?.live?.energyKWh,
      cumulativeMeter: state?.live?.cumulativeMeter,
      meterStart: state?.live?.meter_start,
    };
    console.log('[ChargeMonitoring] live data gate', gateValues);
  }, [
    state?.live?.hasReceivedMeterValues,
    state?.live?.lifecycleState,
    state?.live?.status,
    state?.live?.energyKWh,
    state?.live?.cumulativeMeter,
    state?.live?.meter_start,
  ]);

  // ============================================================
  // PART 5: Cleanup on Unmount
  // Stop all timers and polling to prevent memory leaks
  // ============================================================
  useEffect(() => {
    return () => {
      // Reset rehydration flag on unmount to allow fresh mount
      hasRehydrated.current = false;
      
      // Cleanup polling interval if it exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        console.log('[ChargeMonitoring] 🧹 Cleaned up polling interval on unmount');
      }
      
      // Cleanup preparing timeout if it exists
      if (preparingTimeoutRef.current) {
        clearTimeout(preparingTimeoutRef.current);
        preparingTimeoutRef.current = null;
        console.log('[ChargeMonitoring] 🧹 Cleaned up preparing timeout on unmount');
      }
    };
  }, []);

  // ============================================================
  // TERMINAL STATE DETECTION
  // Check if session is in a terminal failure state that should override all UI
  // Terminal states: expired, failed, voided, cancelled (from backend session status)
  // ============================================================
  const isTerminalFailure = !!startFailedError || state?.sessionStartFailed === true;
  
  // ============================================================
  // LIVE DATA GATE: Determine if live charging data should be displayed
  // Once MeterValues have arrived, show live data regardless of REST status.
  // This prevents the UI from staying stuck on "Waiting for live data..."
  // when SSE MeterValues are flowing but REST polling still says "preparing".
  // ============================================================
  const hasLiveData =
    state?.live?.hasReceivedMeterValues === true ||
    (typeof state?.live?.energyKWh === 'number' && state.live.energyKWh > 0) ||
    (state?.live?.cumulativeMeter != null && state.live.cumulativeMeter > 0) ||
    (state?.live?.meter_start != null);

  // First-hand evidence that the charger is delivering energy to THIS session.
  // Used as the canonical gate for the "No energy detected" banner so the
  // banner reflects what the charger has actually shown us, not what the
  // session record says happened in the past.
  const hasChargingEvidence = hasActiveMeterActivity(state?.live);
  // Time since MONITOR_CHARGING mounted (re-evaluated each render; the
  // elapsedMs 1s ticker is enough to drive re-renders).
  const localObservationMs = Date.now() - monitoringStartedAt.current;

  // ============================================================
  // BACKEND-AUTHORITATIVE CHARGING STATE
  // Stop button must be shown when session has charging indicators
  // CRITICAL: Check multiple signals for active charging session
  // ============================================================
  const isPreparing = state?.live?.status === 'Preparing';
  const isCharging = state?.live?.status === 'Charging' || !!state?.live?.tx_id || hasLiveData;
  // One derivation for the banner AND the heading, from the authoritative
  // session state (chargingActivity / batteryFullSince / isIdle), so the page
  // never says "Charging in progress" above a "Charging paused" banner.
  const chargingPresentation = isCharging
    ? deriveChargingPresentation(state?.live)
    : { kind: 'charging' };
  const vehiclePaused = PAUSED_KINDS.has(chargingPresentation.kind);
  // Charger UX: when the API provides the session's canonical snapshot, the
  // heading and the status card come from it and the local banners below are
  // not shown (one presentation per event). Without one, nothing changes.
  const { snapshot: canonicalSnapshot } = useCanonicalSession(state?.sessionUid);
  const hasCanonical = !!canonicalSnapshot;
  const canonicalView = hasCanonical ? presentSnapshot(canonicalSnapshot) : null;
  
  // CRITICAL: Session is considered "chargeable" (stop button should show) if:
  // - Has startTime (charging started)
  // - Has tx_id (transaction active)
  // - Status is Charging or Preparing
  // This ensures stop button shows even during recovery when tx_id is temporary
  const hasChargingSession = 
    Boolean(state?.live?.startTime) || 
    Boolean(state?.live?.tx_id) || 
    state?.live?.status === 'Charging' ||
    state?.live?.status === 'Preparing';

  // The estimate rows. When this screen already says paused but the estimate
  // has not caught up, the rate still reads "Paused" and an energy-based time
  // left "—" (a per-minute time left keeps counting: the tariff bills by the
  // minute whether or not power flows).
  const estimateRows = describeLiveEstimate(liveEstimate, {
    clientPaused: vehiclePaused,
    fallbackBillingUnit: storedBillingUnit,
  });
  const estimateValue = (part, loadingText) => (
    part.pending
      ? ((isPreparing || isCharging) ? <LoadingText text={loadingText} /> : '—')
      : part.text
  );
  // The cost row is hidden for a session that costs nothing. Without an
  // estimate that is read from the stored tariff (is there a rate at all?),
  // never from a calculation.
  const storedRate = storedBillingUnit === 'minute'
    ? state?.pricingSnapshot?.rate_per_minute
    : state?.pricingSnapshot?.rate_per_kwh;
  const freeVehicle = !!state?.vehicle?.verified
    && state?.vehicle?.billingMode === 'FREE'
    && state?.vehicle?.rateApplied === 0;
  const showEstimatedCost = estimate.hasEstimate
    ? !estimate.noCharge
    : (!freeVehicle && Number(storedRate) > 0);
  const hasSessionStart = Number.isFinite(new Date(state?.live?.startTime || NaN).getTime());

  // Format start time
  const startTime = useMemo(() => {
    if (!state?.live?.startTime) return '—';
    try {
      const date = new Date(state.live.startTime);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '—';
    }
  }, [state?.live?.startTime]);

  // ============================================================
  // ACTION BUTTONS
  // Show "Stop Charging" for active charging sessions
  // CRITICAL: Use hasChargingSession check (not just isCharging)
  // This ensures stop button shows during recovery scenarios
  // ============================================================
  const actions = [];
  if (!isTerminalFailure && hasChargingSession) {
    actions.push({
      id: 'stop-charging',
      label: 'Stop Charging',
      type: 'btn-danger',
      data: { stationId: state.stationId, connectorId: state.connectorId }
    });
    // Show force-end as secondary option when stop is unresponsive
    if (showForceEnd) {
      actions.push({
        id: 'force-end',
        label: 'Force End Session',
        type: 'btn-outline-danger'
      });
    }
  }

  return (
    <div className="ev-content-wrapper">
      <EvHeader
        headerAction={{
          label: 'Charging',
          action: null,
        }}
      />

      <div className="ev-inner-profile-wrapper charge-monitor-container">
        {/* ============================================================
            TERMINAL FAILURE STATE - Override all other UI
            When session is expired/failed/voided/cancelled, show ONLY error state
            Hide all active charging UI (header, preparing banner, stop button, etc.)
            ============================================================ */}
        {isTerminalFailure ? (
          <div className="cm-terminal-failure">
            <div className="cm-header-icon">
              <div className="cm-header-circle cm-header-circle-error">
                <FiBatteryCharging size={32} />
              </div>
            </div>
            <h2 className="cm-title cm-title-error">Session expired</h2>
            <p className="cm-subtitle">
              The charging session could not be started and has been terminated.
            </p>
          </div>
        ) : (
          <>
            {/* Payment recovery: first thing on the screen, above the status
                heading, and never in the way of the controls below. Renders
                nothing unless the API asks for a card confirmation. */}
            <PaymentRecoveryPrompt
              sessionUid={state.sessionUid}
              prompt={paymentRecoveryPrompt}
            />
            {/* Icon + title - ONLY for active sessions */}
            <div className="cm-header-icon">
              <div className="cm-header-circle">
                <FiBatteryCharging size={32} />
              </div>
            </div>

            <h2 className="cm-title" data-testid="cm-title">
              <FaCircle
                className={hasCanonical
                  ? (canonicalSnapshot.sessionState === 'CHARGING' ? 'cm-live-dot' : 'cm-live-dot cm-live-dot-paused')
                  : (vehiclePaused ? 'cm-live-dot cm-live-dot-paused' : 'cm-live-dot')}
                aria-hidden="true"
              />
              {hasCanonical
                ? canonicalView.heading
                : (vehiclePaused ? 'Charging paused' : 'Charging in progress')}
            </h2>
            {hasCanonical && (
              <LiveCanonicalStatus
                sessionUid={state.sessionUid}
                onRefresh={rehydrateSession}
              />
            )}
            {/*
              Show the driver which physical connector their session
              is on. Multi-connector chargers used to silently pin
              every customer to connector 1 (see the audit); now that
              the picker on EvProfile records the real choice, we
              surface it here so the driver can confirm the app is
              tracking the port they plugged into.

              Rendered ONLY when we have a real physical connector id
              (>= 1). Legacy sessions predating the picker (or a mid-
              session rehydration without a connector) show the
              existing subtitle unchanged — we never fabricate
              "Connector 1" for a session whose true connector is
              unknown.
            */}
            {Number.isInteger(state.connectorId) && state.connectorId >= 1 ? (
              <p className="cm-subtitle" data-testid="cm-connector-label">
                Connector {state.connectorId} · live session values update automatically.
              </p>
            ) : (
              <p className="cm-subtitle">
                Live session details below. Values update automatically.
              </p>
            )}

            {/* Inline Preparing banner - shown when charger is preparing but not yet delivering energy.
                Solid amber variant carries the "high-attention but not failure" weight the prior UX had. */}
            {!hasCanonical && isPreparing && !isCharging && energyDelivered === 0 && (
              <Notice
                className="cm-preparing-banner"
                variant="warning-solid"
                title={preparingTimeoutWarning ? 'Taking longer than expected' : 'Preparing to start'}
              >
                {preparingTimeoutWarning
                  ? 'The charger is taking longer than usual. Please ensure the cable is properly connected.'
                  : 'Your charger is getting ready. Please do not unplug the charger.'}
              </Notice>
            )}

            {/* No energy warning - charger reports charging but no first-hand
                MeterValues evidence has arrived since this monitoring mount.
                Anchored to monitoringStartedAt (not state.live.startTime) so a
                rehydrated mid-session never trips the banner instantly. */}
            {!hasCanonical && isCharging && !hasChargingEvidence && localObservationMs > 180000 && (
              <Notice
                className="cm-preparing-banner"
                variant="warning"
                title="No energy detected"
              >
                The charger appears to be connected but no energy has been delivered. Please check that the cable is firmly plugged in on both ends.
              </Notice>
            )}

            {/*
              Idle / battery-full banner.
              Backend sessionIdleDetector flips `live.isIdle` via
              SESSION_IDLE_CHANGED once power has been < threshold and energy
              has been flat for the evaluation window (default 8 min past a
              5-min warmup). We also derive a local "stream stalled" signal
              from `lastReadingAt` so we can surface a degraded state even
              when the backend hasn't fired yet (e.g. network drop to
              charger). Priority: stream stalled → long idle → short idle.
            */}
            {!hasCanonical && isCharging && (() => {
              const p = chargingPresentation;

              // Stream stalled — no MeterValues for >10 min on an active session.
              if (p.kind === 'stalled') {
                return (
                  <Notice
                    className="cm-preparing-banner"
                    variant="warning"
                    title="Connection lost"
                  >
                    We haven't received an update from the charger in a while. If your vehicle is finished, you can safely unplug.
                  </Notice>
                );
              }

              // A backend paused/full flag overridden because the session is
              // observably progressing (recent energy, or power > 0.1 kW).
              if (p.suppressed) {
                console.log(`[ChargeMonitoring] ${p.suppressed === 'full' ? 'Charge-complete' : 'Paused'} banner suppressed — session is progressing`, {
                  reason: p.suppressedBy,
                  activity: state?.live?.chargingActivity,
                  batteryFullSince: state?.live?.batteryFullSince,
                  isIdle: state?.live?.isIdle,
                  powerKW: state?.live?.powerKW,
                  energyKWh: state?.live?.energyKWh,
                  lastEnergyIncreaseAt: state?.live?.lastEnergyIncreaseAt,
                });
                return null;
              }

              // Battery likely full (backend sessionIdleDetector's stricter tier).
              if (p.kind === 'full') {
                return (
                  <Notice
                    className="cm-preparing-banner cm-preparing-banner-complete"
                    variant="warning"
                    title="Charging paused"
                    data-testid="cm-banner-charge-complete"
                  >
                    {/* Circumstantial evidence only (the vehicle stopped
                        requesting energy): never claim the battery is full,
                        and never ask the driver to stop a session that
                        GreenCharge's auto-stop is about to end. */}
                    Your vehicle has stopped drawing power and may be fully charged.
                  </Notice>
                );
              }

              // Legacy 30+ min idle fallback — kept for when the backend
              // battery-full detection feature flag is OFF.
              if (p.kind === 'idle_long') {
                return (
                  <Notice
                    className="cm-preparing-banner"
                    variant="warning"
                    title="Battery may be full"
                  >
                    No energy has been drawn for over 30 minutes. Your vehicle may be fully charged — please unplug when you're ready so others can use the charger.
                  </Notice>
                );
              }

              if (p.kind === 'paused') {
                return (
                  <Notice
                    className="cm-preparing-banner cm-preparing-banner-paused"
                    variant="warning"
                    title="Charging paused"
                    data-testid="cm-banner-charge-paused"
                  >
                    Your vehicle is temporarily not drawing power. Charging may resume automatically.
                  </Notice>
                );
              }

              return null;
            })()}

            {/* Reconnecting state - shown during session rehydration */}
            {isReconnecting ? (
              <div className="cm-waiting-state">
                <div className="cm-waiting-spinner" />
                <p>Reconnecting to session...</p>
              </div>
            ) : (!hasSessionStart && !hasLiveData) ? (
              /* Waiting for live data — only shown when NEITHER a start time
                 NOR any MeterValues evidence exists yet. Once hasLiveData is
                 true (hasReceivedMeterValues / energyKWh / cumulativeMeter /
                 meterStart), we skip this gate and show the live panel. */
              <div className="cm-waiting-state">
                <div className="cm-waiting-spinner" />
                <p>Waiting for live data...</p>
              </div>
            ) : (
              <>
                {/* Per-minute budget warning — last 20% of session (minimum 30s).
                    A <Notice>, like every other banner on this step: the old
                    bare heading-and-paragraph markup had no styles left and
                    rendered as loose text. */}
                {isBudgetAlmostReached(estimate) && (
                  <Notice
                    className="cm-preparing-banner"
                    variant="warning"
                    title="Budget almost reached"
                  >
                    {budgetAlmostReachedLead(estimate)}
                    {' '}Charging will stop automatically when your session budget is reached.
                  </Notice>
                )}

                {/* Per-minute budget reached — auto-stopping */}
                {billingUnit === 'minute' && estimate.budgetReached && (
                  <Notice
                    className="cm-preparing-banner"
                    variant="warning"
                    title="Session budget reached"
                  >
                    Your pre-authorized budget has been used. The charger is being stopped automatically.
                  </Notice>
                )}

                <InfoPanel>
                  <LabelValueRow label="Started" value={startTime} />
                  <LabelValueRow
                    label={billingUnit === 'minute' ? 'Billing Duration' : 'Elapsed Time'}
                    value={state?.live?.startTime ? formatElapsedTime(elapsedMs) : '—'}
                  />

                  {/* Time Left - hidden for FREE charging sessions (no time limit) */}
                  {state?.vehicle?.paymentRequired !== false && (
                    <LabelValueRow
                      label={billingUnit === 'minute' ? 'Time Remaining' : 'Est. Time Left'}
                      value={estimateValue(estimateRows.timeRemaining, 'Calculating')}
                    />
                  )}

                  {/* Energy Delivered - always shown for per-kWh; secondary info for per-minute (only when data available) */}
                  {(billingUnit !== 'minute' || energyDelivered > 0) && (
                    <LabelValueRow
                      label="Energy Delivered"
                      value={
                        energyDelivered > 0
                          ? `${energyDelivered.toFixed(2)} kWh`
                          : (isPreparing || isCharging) ? <LoadingText text="Receiving data" /> : '0.00 kWh'
                      }
                    />
                  )}
                  <LabelValueRow
                    label={billingUnit === 'minute' ? 'Billing Rate' : 'Charge Rate'}
                    value={
                      billingUnit === 'minute'
                        ? (state?.pricingSnapshot?.rate_per_minute != null
                            ? `${formatCurrency(state.pricingSnapshot.rate_per_minute, state?.pricingSnapshot?.currency || 'BBD')}/min`
                            : '—')
                        : estimateValue(estimateRows.chargeRate, 'Measuring')
                    }
                  />
                  {showEstimatedCost && (
                    <LabelValueRow
                      label="Estimated Cost"
                      value={estimateValue(estimateRows.cost, 'Calculating')}
                    />
                  )}
                  
                  {/* Progress Bar as last row inside InfoPanel */}
                  <div className="cm-progress-row">
                    {estimate.progressPercent !== null ? (
                      <>
                        <div className="cm-progress-header">
                          <span className="cm-progress-label">
                            {estimate.progressPercent >= 100
                              ? (billingUnit === 'minute' ? 'Session Budget Reached' : 'Session Complete')
                              : (billingUnit === 'minute' ? 'Budget Usage' : 'Estimated Progress')}
                          </span>
                          <span className="cm-progress-percentage">{Math.round(estimate.progressPercent)}%</span>
                        </div>
                        <div className="cm-progress-track">
                          <div
                            className={`cm-progress-fill${
                              state?.live?.status === 'Charging' && !state?.live?.endTime && !isTerminalFailure ? ' is-live' : ''
                            }${
                              billingUnit === 'minute' && estimate.progressPercent >= 100 ? ' cm-progress-fill-danger' :
                              billingUnit === 'minute' && estimate.progressPercent >= 85 ? ' cm-progress-fill-warning' : ''
                            }`}
                            style={{ width: `${estimate.progressPercent}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="cm-progress-header">
                          <span className="cm-progress-label">Charging Live</span>
                        </div>
                        <div className="cm-progress-track">
                          <div className="cm-progress-fill-indeterminate" />
                        </div>
                      </>
                    )}
                  </div>
                </InfoPanel>
              </>
            )}
          </>
        )}
      </div>

      <ActionButtonWrapper ref={ref} actions={actions} />

      {/* Start Failure Error Modal */}
      <Modal show={!!startFailedError} centered size="sm" onHide={() => {}} aria-labelledby="cm-start-failed-title">
        <Modal.Header>
          <Modal.Title id="cm-start-failed-title">Unable to start charging</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{startFailedError?.message || "We couldn't start the charger. Please try again."}</p>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Check the cable connection and try again.
          </p>
          {startFailedError?.correlationId && (
            <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
              Ref: {startFailedError.correlationId}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button 
            className="btn btn-dark" 
            onClick={handleRetry}
            disabled={isRetrying || isCancelling}
          >
            {isRetrying ? 'Retrying...' : 'Try again'}
          </button>
          <button 
            className="btn btn-outline-dark" 
            onClick={handleCancelSession}
            disabled={isRetrying || isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel session'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChargeMonitoring;
